import React from "react";
import { FetchPolicy } from "@apollo/client";
import { useNavigation } from "@navigation";
import { IconButton, Grid, Paper, Typography, Chip } from "@material-ui/core";
import { Edit } from "@material-ui/icons";
import { useGetCustomerContractCardQuery } from "@graphql";
import { Body, ErrorCard, Spinner } from "@components";
import { CustomerContractEditorLocation } from "../locations/CustomerContractEditorLocation";
import { useLocale, useUserProfile } from "@hooks";
import { formatDate } from "../utils";
import WorkIcon from "@material-ui/icons/Work";
import { toLocale } from "@bonliva-crm/locale";

type Props = {
  id: string;
  hideTitle?: boolean;
  fetchPolicy?: FetchPolicy;
};

export function CustomerContractCard(props: Props) {
  const { fetchPolicy = "cache-first" } = props;

  const navigation = useNavigation();
  const { locale } = useLocale();
  const { can } = useUserProfile();

  const { loading, data, error } = useGetCustomerContractCardQuery({
    variables: { id: props.id },
    fetchPolicy
  });

  const { customerContractById: profile } = data;

  if (loading) {
    return <Spinner />;
  }

  if (error) {
    return <ErrorCard message={error.message} title={error.name} />;
  }

  const { customerContractCard } = locale;

  const profs = profile.professions
    .sortBy((x) => x)
    .unique()
    .join(", ");

  return (
    <Paper style={{ padding: 16, position: "relative" }}>
      <div style={{ display: "flex", marginBottom: 8 }}>
        <Typography variant="h6" style={{ fontWeight: 1000 }}>
          {locale.customerContractProfile.customercontract}:
        </Typography>
        <Typography variant="h6" style={{ fontWeight: 400, marginLeft: 4 }}>
          {data.customerContractById.contractName}{" "}
          {data.customerContractById.contractNumber}
        </Typography>

        {data.customerContractById?.typeOfAgreement === "Procured" && (
          <Chip
            avatar={
              <WorkIcon
                fontSize="small"
                style={{ color: "#ff857a", background: "none" }}
              />
            }
            color="primary"
            variant="outlined"
            style={{ marginLeft: 16 }}
            label={<Body>{locale.customerContractAgreementType.Procured}</Body>}
          />
        )}

        {data.customerContractById?.typeOfAgreement === "NotProcured" && (
          <Chip
            avatar={
              <WorkIcon
                fontSize="small"
                style={{ color: "#b0b0b0", background: "none" }}
              />
            }
            variant="outlined"
            style={{ marginLeft: 16, color: "#b0b0b0" }}
            label={<Body>{locale.customerContractAgreementType.NotProcured}</Body>}
          />
        )}

        {data.customerContractById?.company && (
          <Chip
            variant="outlined"
            style={{ marginLeft: 16 }}
            label={
              <Body>
                {locale.customerContractCompany[data.customerContractById?.company]}
              </Body>
            }
          />
        )}
      </div>
      <div style={{ position: "absolute", right: 8, top: 8 }}>
        <IconButton
          onClick={() =>
            navigation.navigate(new CustomerContractEditorLocation(profile._id))
          }
          disabled={!can("customer-contract:edit:all")}
        >
          <Edit />
        </IconButton>
      </div>

      <Grid container spacing={1}>
        {!props.hideTitle && (
          <>
            <Grid item xs={3}>
              <Body style={{ fontWeight: "bold" }}>
                {customerContractCard.contractNumber}:
              </Body>
            </Grid>
            <Grid item xs={9}>
              {profile.contractNumber}
            </Grid>
          </>
        )}

        {profs && (
          <>
            <Grid item xs={3}>
              <Body style={{ fontWeight: "bold" }}>
                {customerContractCard.profession}:
              </Body>
            </Grid>
            <Grid item xs={9}>
              {profs}
            </Grid>
          </>
        )}

        <Grid item xs={3}>
          <Body style={{ fontWeight: "bold" }}>{customerContractCard.startDate}:</Body>
        </Grid>
        <Grid item xs={9}>
          {formatDate(profile.contractStart)}
        </Grid>

        <Grid item xs={3}>
          <Body style={{ fontWeight: "bold" }}>{customerContractCard.endDate}:</Body>
        </Grid>
        <Grid item xs={9}>
          {formatDate(profile.contractEnd)}
        </Grid>

        <Grid item xs={3}>
          <Body style={{ fontWeight: "bold" }}>{customerContractCard.currentEnd}:</Body>
        </Grid>
        <Grid item xs={9}>
          {formatDate(profile.currentContractEnd)}
        </Grid>

        <Grid item xs={3}>
          <Body style={{ fontWeight: "bold" }}>
            {customerContractCard.prolongationDate}:
          </Body>
        </Grid>
        <Grid item xs={9}>
          {formatDate(profile.contractProlongation)}
        </Grid>

        <Grid item xs={3}>
          <Body style={{ fontWeight: "bold" }}>
            {customerContractCard.prolongationDateDeadline}:
          </Body>
        </Grid>
        <Grid item xs={9}>
          {formatDate(profile.contractProlongationDeadline)}
        </Grid>

        <Grid item xs={3}>
          <Body style={{ fontWeight: "bold" }}>
            {locale.customerContractEdit.proLongationComment}:
          </Body>
        </Grid>
        <Grid item xs={9}>
          {data.customerContractById?.prolongationComment}
        </Grid>
        <Grid item xs={3}>
          <Body style={{ fontWeight: "bold" }}>
            {customerContractCard.priceAdjustmentDate}:
          </Body>
        </Grid>
        <Grid item xs={9}>
          {formatDate(profile.contractPriceAdjustmentDate)}
        </Grid>
        <Grid item xs={3}>
          <Body style={{ fontWeight: "bold" }}>
            {customerContractCard.priceAdjustmentDateDeadline}:
          </Body>
        </Grid>
        <Grid item xs={9}>
          {formatDate(profile.contractPriceAdjustmentDateDeadline)}
        </Grid>
        <Grid item xs={3}>
          <Body style={{ fontWeight: "bold" }}>
            {locale.customerContractEdit.adjustmentComment}:
          </Body>
        </Grid>
        <Grid item xs={9}>
          {data.customerContractById?.adjustmentComment}
        </Grid>
        <Grid item xs={3}>
          <Body style={{ fontWeight: "bold" }}>
            {locale.customerContractEdit.estimatedContractValue}:
          </Body>
        </Grid>
        <Grid item xs={9}>
          {data.customerContractById?.estimatedContractValue &&
            toLocale.sek(data.customerContractById?.estimatedContractValue)}
        </Grid>
        {data.customerContractById?.dispenseDeadline && (
          <>
            <Grid item xs={3}>
              <Body style={{ fontWeight: "bold" }}>
                {locale.customerContractEdit.dispenseDeadline}:
              </Body>
            </Grid>
            <Grid item xs={9}>
              {data.customerContractById?.dispenseDeadline}
            </Grid>
          </>
        )}
        {profile.introductionInfo && (
          <>
            <Grid item xs={3}>
              <Body style={{ fontWeight: "bold" }}>
                {customerContractCard.introductionInfo}:
              </Body>
            </Grid>
            <Grid item xs={9}>
              {profile.introductionInfo}
            </Grid>
          </>
        )}

        {profile.overtime && (
          <>
            <Grid item xs={3}>
              <Body style={{ fontWeight: "bold" }}>{locale.AdditionalWork}:</Body>
            </Grid>
            <Grid item xs={9}>
              {profile.overtimeNote}
            </Grid>
          </>
        )}

        {profile.possibleInvoiceForAccommodation && (
          <>
            <Grid item xs={3}>
              <Body style={{ fontWeight: "bold" }}>
                {locale.PossibleInvoiceForAccommodation}:
              </Body>
            </Grid>
            <Grid item xs={9}>
              {profile.possibleInvoiceForAccommodationNote}
            </Grid>
          </>
        )}

        {profile.possibleInvoiceForTravel && (
          <>
            <Grid item xs={3}>
              <Body style={{ fontWeight: "bold" }}>
                {locale.PossibleInvoiceForTravel}:
              </Body>
            </Grid>
            <Grid item xs={9}>
              {profile.possibleInvoiceForTravelNote}
            </Grid>
          </>
        )}

        {profile.overtimeOption && (
          <>
            <Grid item xs={3}>
              <Body style={{ fontWeight: "bold" }}>{locale.OvertimeOptionOBNote}:</Body>
            </Grid>
            <Grid item xs={9}>
              {profile.overtimeOptionOBNote}
            </Grid>
          </>
        )}
      </Grid>
    </Paper>
  );
}
